import { useState, useRef, useEffect, useCallback } from "react";
import Papa from "papaparse";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { UploadCloud, Download, FileSpreadsheet, CheckCircle2, XCircle, Layers, Crown, RefreshCw } from "lucide-react";

const COLUMNS = ["property_type", "beds", "baths", "sqft", "features", "location", "nearby", "tone", "target_buyer", "platform", "title"];
const TEMPLATE = `property_type,beds,baths,sqft,features,location,nearby,tone,target_buyer,platform,title
Single-family home,4,3,2400,"Chef's kitchen, vaulted ceilings, heated pool, 3-car garage",Highland Park Austin TX,"Top-rated schools, lakefront trails, cafes",luxury,move-up buyers,Instagram,123 Maple Ave
Condo,2,2,1100,"Floor-to-ceiling windows, rooftop deck, smart home",South Loop Chicago IL,"Transit, dining, parks",modern,young professionals,Facebook,Unit 1402 Lakeview`;

function downloadTemplate() {
  const blob = new Blob([TEMPLATE], { type: "text/csv" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = "penhausai_bulk_template.csv";
  a.click();
  URL.revokeObjectURL(url);
}

function UpgradeGate({ onGoBilling }) {
  return (
    <div className="bg-white border border-slate-200 rounded-lg p-10 text-center max-w-xl mx-auto" data-testid="bulk-upgrade-gate">
      <div className="h-12 w-12 rounded-full bg-[#D4AF37]/15 flex items-center justify-center mx-auto">
        <Crown className="h-6 w-6 text-[#a8861a]" />
      </div>
      <h2 className="font-semibold text-xl mt-5">Bulk generation is a Brokerage feature</h2>
      <p className="text-slate-500 mt-2">Upload a spreadsheet of properties and generate a full marketing kit for every listing at once — built for teams and high-volume offices.</p>
      <Button onClick={onGoBilling} className="mt-6 bg-slate-900 hover:bg-slate-800 text-white" data-testid="bulk-upgrade-btn">
        Upgrade to Brokerage
      </Button>
    </div>
  );
}

export default function BulkView({ onGoBilling, onUsed }) {
  const { user } = useAuth();
  const isBrokerage = user?.plan === "brokerage" || user?.role === "admin";
  const [rows, setRows] = useState([]);
  const [fileName, setFileName] = useState("");
  const [job, setJob] = useState(null);
  const [starting, setStarting] = useState(false);
  const fileRef = useRef(null);
  const pollRef = useRef(null);

  const onFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setFileName(file.name);
    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: (res) => {
        const parsed = res.data
          .map((r) => {
            const obj = {};
            COLUMNS.forEach((c) => { obj[c] = (r[c] || "").toString().trim(); });
            return obj;
          })
          .filter((r) => r.features || r.location);
        if (parsed.length === 0) {
          toast.error("No valid rows found. Use the template columns.");
          return;
        }
        setRows(parsed);
        setJob(null);
        toast.success(`${parsed.length} listing${parsed.length > 1 ? "s" : ""} ready to generate.`);
      },
      error: () => toast.error("Couldn't parse that CSV."),
    });
  };

  const poll = useCallback(async (jobId) => {
    try {
      const { data } = await api.get(`/bulk/jobs/${jobId}`);
      setJob(data);
      if (data.status === "complete") {
        clearInterval(pollRef.current);
        onUsed?.();
        toast.success(`Batch complete — ${data.completed} generated, ${data.failed} failed.`);
      }
    } catch {
      clearInterval(pollRef.current);
    }
  }, [onUsed]);

  const start = async () => {
    setStarting(true);
    try {
      const { data } = await api.post("/bulk/generate", { rows });
      setJob({ id: data.job_id, total: data.total, completed: 0, failed: 0, status: "processing", results: [] });
      pollRef.current = setInterval(() => poll(data.job_id), 2500);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setStarting(false);
    }
  };

  useEffect(() => () => clearInterval(pollRef.current), []);

  if (!isBrokerage) return <UpgradeGate onGoBilling={onGoBilling} />;

  const done = job ? job.completed + job.failed : 0;
  const pct = job && job.total ? Math.round((done / job.total) * 100) : 0;

  return (
    <div className="max-w-3xl space-y-6">
      <div className="bg-white border border-slate-200 rounded-lg p-6">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h2 className="font-semibold text-lg flex items-center gap-2"><Layers className="h-5 w-5" /> Bulk listing generator</h2>
            <p className="text-sm text-slate-500 mt-1">Upload a CSV of properties (up to 25) and generate a full marketing kit for each — all saved to your library.</p>
          </div>
          <Button variant="outline" onClick={downloadTemplate} data-testid="bulk-template-btn"><Download className="h-4 w-4 mr-2" /> CSV template</Button>
        </div>

        <div onClick={() => fileRef.current?.click()} data-testid="bulk-dropzone"
          className="mt-5 border-2 border-dashed border-slate-300 rounded-lg p-10 text-center cursor-pointer hover:border-slate-400 transition-colors">
          <UploadCloud className="h-9 w-9 text-slate-400 mx-auto" />
          <p className="text-slate-600 mt-3 font-medium">{fileName || "Click to upload a CSV"}</p>
          <p className="text-xs text-slate-400 mt-1">Columns: property_type, beds, baths, sqft, features, location, nearby, tone, target_buyer, platform, title</p>
        </div>
        <input ref={fileRef} type="file" accept=".csv,text/csv" className="hidden" onChange={onFile} data-testid="bulk-file-input" />

        {rows.length > 0 && !job && (
          <div className="mt-5">
            <div className="flex items-center gap-2 text-sm text-slate-600 mb-3">
              <FileSpreadsheet className="h-4 w-4" /> {rows.length} listing{rows.length > 1 ? "s" : ""} parsed
            </div>
            <div className="border border-slate-200 rounded-md divide-y divide-slate-100 max-h-52 overflow-auto">
              {rows.slice(0, 8).map((r, i) => (
                <div key={i} className="px-4 py-2.5 text-sm flex justify-between gap-4">
                  <span className="font-medium truncate">{r.title || `${r.beds} bed ${r.property_type}`}</span>
                  <span className="text-slate-400 truncate">{r.location}</span>
                </div>
              ))}
              {rows.length > 8 && <div className="px-4 py-2 text-xs text-slate-400">+ {rows.length - 8} more…</div>}
            </div>
            <Button onClick={start} disabled={starting} className="mt-4 h-11 bg-slate-900 hover:bg-slate-800 text-white font-semibold w-full" data-testid="bulk-start-btn">
              {starting ? "Starting…" : `Generate ${rows.length} marketing kit${rows.length > 1 ? "s" : ""}`}
            </Button>
          </div>
        )}
      </div>

      {job && (
        <div className="bg-white border border-slate-200 rounded-lg p-6" data-testid="bulk-progress">
          <div className="flex items-center justify-between mb-3">
            <span className="font-semibold flex items-center gap-2">
              {job.status === "complete" ? <CheckCircle2 className="h-5 w-5 text-green-600" /> : <RefreshCw className="h-5 w-5 animate-spin text-slate-500" />}
              {job.status === "complete" ? "Batch complete" : "Generating…"}
            </span>
            <span className="text-sm text-slate-500" data-testid="bulk-progress-count">{done} / {job.total}</span>
          </div>
          <Progress value={pct} className="h-2" />
          {(job.results || []).length > 0 && (
            <ul className="mt-4 space-y-2 max-h-64 overflow-auto">
              {job.results.map((r, i) => (
                <li key={i} className="flex items-center gap-2 text-sm">
                  {r.status === "done" ? <CheckCircle2 className="h-4 w-4 text-green-600 shrink-0" /> : <XCircle className="h-4 w-4 text-red-500 shrink-0" />}
                  <span className="truncate">{r.title}</span>
                  {r.status === "error" && <span className="text-xs text-red-500 ml-auto">{r.error}</span>}
                </li>
              ))}
            </ul>
          )}
          {job.status === "complete" && <p className="text-sm text-slate-500 mt-4">All generated listings are saved in <strong>My Listings</strong>.</p>}
        </div>
      )}
    </div>
  );
}
